import {
    Button,
    ButtonGroup,
    DATE_FORMAT,
    DateField,
    DateTimeUtils,
    IButtonGroupOption,
    IPublicRestaurant,
    Label,
    RestaurantUtils,
    SERVER_DATE_FORMAT,
    TIME_FORMAT,
    TLanguageKey,
} from '@localina/core';
import { AddCircleIcon, CalendarIcon, ClockIcon, DateIcon, HouseWithDots, PeopleIcon } from '@localina/icons';
import { formatNumber } from 'libphonenumber-js';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Field } from '../../../components';
import Participants from '../../../components/Participants/Participants';
import { IApplicationStore, IAreaOverview, IReservationDateTime, IShiftOverview } from '../../../interfaces';
import { reservationActions, restaurantActions } from '../../../store';
import { sortItemsBySortedIdsArray } from '../../../utils/SortingUtils';

interface IActionProps {
    requestRestaurantAvailableTimes: typeof restaurantActions.restaurantAvailableTimesRequested;
    saveReservationDateTime: typeof reservationActions.saveReservationDateTime;
    getShifts: typeof restaurantActions.shiftsRequested;
}

interface IStoreProps {
    reservation: IReservationDateTime;
    isLoading: boolean;
    shifts: IShiftOverview[];
}

interface IProps {
    restaurant: IPublicRestaurant;
    onNext: () => void;
}

// four rows of three in a narrow frame, two rows of six when wide
const defaultSlots = 12;

const ParticipantsDateTime: React.FC<IProps & IStoreProps & IActionProps> = (props) => {
    const { i18n, t } = useTranslation();

    const [reservation, setReservation] = React.useState(props.reservation);
    const [isDatePickerOpen, setDatePickerOpen] = React.useState(false);

    const restaurantPhoneNumberFormatted =
        formatNumber(props.restaurant.phoneNumber, 'INTERNATIONAL', { humanReadable: true }) ||
        props.restaurant.phoneNumber;

    const areas = useMemo(() => {
        return props.shifts
            .flatMap((s) => s.areas)
            .reduce((acc, area) => {
                let val = acc[area.id];
                if (!val) {
                    val = { ...area };
                } else {
                    val.slots = [
                        ...val.slots,
                        ...area.slots.filter((sl) => !val?.slots.some((s1) => s1.timeSlot === sl.timeSlot)),
                    ];
                }
                acc[area.id] = val;
                return acc;
            }, {} as { [key: string]: IAreaOverview });
    }, [props.shifts]);

    const areaOptions = useMemo(() => {
        return Object.values(areas).map((area) => ({
            label: area.name,
            value: area.id,
            className: 'area-time',
        }));
    }, [areas]);

    sortItemsBySortedIdsArray(areaOptions, props.restaurant.orderings?.areas || [], 'value');

    useEffect(() => {
        if (!reservation.date || !reservation.participants) {
            return;
        }
        props.getShifts(
            DateTime.fromFormat(reservation.date, DATE_FORMAT).toFormat(SERVER_DATE_FORMAT),
            Number(reservation.participants),
        );
        setReservation((prev) => {
            return {
                ...prev,
                time: '',
            };
        });
    }, [reservation.date, reservation.participants]);

    useEffect(() => {
        if (!areaOptions.length) {
            setReservation((prev) => ({
                ...prev,
                shiftId: '',
                areaId: '',
                shiftTime: '',
                time: '',
            }));
        }
    }, [areaOptions]);

    useEffect(() => {
        if (reservation.areaId) {
            const slotsTimes = areas[reservation.areaId].slots.map(({ timeSlot }) => timeSlot);
            if (!slotsTimes.includes(reservation.time)) {
                setReservation((prev) => {
                    return {
                        ...prev,
                        shiftTime: '',
                        time: '',
                    };
                });
            }
        }
    }, [reservation.areaId]);

    useEffect(() => {
        const main = document.getElementsByTagName('main')[0];
        main.style.scrollBehavior = 'smooth';

        if (props.reservation.time !== '') {
            setReservation((prev) => {
                return {
                    ...prev,
                    showAllTimes: true,
                };
            });
        }
    }, []);

    const scrollDown = () => {
        // Timeout needed in order to let react inject first child node
        setTimeout(() => {
            const main = document.getElementsByTagName('main')[0];
            main.scrollTop = main.scrollHeight;
        });
    };

    // e.g. "Freitag, 2. Oktober 2026"; plain "en" would give the US month-first order
    const formatLongDate = (date: string) =>
        DateTime.fromFormat(date, DATE_FORMAT)
            .setLocale(i18n.language === 'en' ? 'en-GB' : i18n.language)
            .toLocaleString(DateTime.DATE_HUGE);

    const isDateDisabled = (date: DateTime) =>
        RestaurantUtils.getShiftOptionsForDate(
            date.toFormat(DATE_FORMAT),
            props.restaurant,
            i18n.language as TLanguageKey,
        ).every((it) => it.disabled);

    const dateOptions = [
        {
            label: t('reservation.datetime.date.today'),
            value: 'today',
            disabled: isDateDisabled(DateTime.local()),
        },
        {
            label: t('reservation.datetime.date.tomorrow'),
            value: 'tomorrow',
            disabled: isDateDisabled(DateTime.local().plus({ day: 1 })),
        },
        {
            label: (
                <>
                    <DateIcon />
                    <span className="widget-sr-only">{t('reservation.datetime.date.pick')}</span>
                </>
            ),
            value: 'date',
            className: 'date-picker-option',
        },
    ];

    const handleDate = (value: string) => {
        let date = '';
        let dateOption = '';

        const today = DateTime.local().toFormat(DATE_FORMAT);
        const tomorrow = DateTime.local().plus({ days: 1 }).toFormat(DATE_FORMAT);

        switch (value) {
            case 'today':
                date = today;
                dateOption = value;
                break;
            case 'tomorrow':
                date = tomorrow;
                dateOption = value;
                break;
            case 'date':
                setDatePickerOpen(true);
                break;
            default:
                date = value;
                dateOption = value === today ? 'today' : value === tomorrow ? 'tomorrow' : 'date';
                setDatePickerOpen(false);
        }
        if (date !== '') {
            setReservation((prev) => {
                return {
                    ...prev,
                    date,
                    dateOption,
                    shiftId: '',
                    areaId: '',
                    shiftTime: '',
                    time: '',
                };
            });
            scrollDown();
        }
    };

    const handleTime = (time: string) => {
        const shift = props.shifts.find((sh) => {
            return DateTimeUtils.isTimeInRange(DateTime.fromFormat(time, TIME_FORMAT), sh.from, sh.to);
        });
        if (!shift) {
            return;
        }
        setReservation((prev) => {
            return {
                ...prev,
                time,
                shiftTime: shift.from,
                shiftId: shift.id,
            };
        });
        scrollDown();
    };

    const handleArea = (areaId: any) => {
        setReservation((prev) => {
            return {
                ...prev,
                time: '',
                areaId,
                showAllTimes: false,
            };
        });
    };

    const handleNext = () => {
        const main = document.getElementsByTagName('main')[0];
        main.style.removeProperty('scroll-behavior');
        props.saveReservationDateTime(reservation);
        props.onNext();
    };

    useEffect(() => {
        if (areaOptions?.length === 1) {
            handleArea(areaOptions[0].value);
        }
    }, [areaOptions?.length, reservation.date]);

    useEffect(() => {
        if (reservation.areaId && areas[reservation.areaId]?.slots?.length) {
            scrollDown();
        }
    }, [reservation.areaId, areas]);

    const selectedArea = reservation.areaId ? areas[reservation.areaId] : undefined;
    const slotOptions: IButtonGroupOption[] = (selectedArea?.slots ?? [])
        .sort((slotA, slotB) => (slotA.timeSlot > slotB.timeSlot ? 1 : -1))
        .map((slot) => {
            let otherButtonProps: Pick<IButtonGroupOption, 'disabled' | 'className' | 'tooltip'> = {
                disabled: false,
                tooltip: undefined,
                className: undefined,
            };
            if (slot.disabled) {
                otherButtonProps = {
                    disabled: true,
                    tooltip: t('reservation.datetime.time.disabledSlotTooltip.restrictedReservationsDuringTheShift', {
                        phoneNumber: restaurantPhoneNumberFormatted,
                    }),
                    className: 'slot-gray',
                };
            } else if (slot.availability - reservation.participants < 0) {
                otherButtonProps = {
                    disabled: true,
                    tooltip: t('reservation.datetime.time.disabledSlotTooltip.slotAlreadyTaken'),
                    className: 'slot-red',
                };
            }
            return {
                label: slot.timeSlot,
                value: slot.timeSlot,
                ...otherButtonProps,
            };
        });

    return (
        <>
            <div className="widget-heading">
                <Label type="title" value={t('reservation.datetime.title')} variant="h5" />
                <p className="widget-lead">{t('reservation.datetime.lead')}</p>
            </div>
            <Field
                className="date"
                icon={<CalendarIcon />}
                label={t('reservation.fields.date')}
                hint={reservation.date !== '' ? formatLongDate(reservation.date) : undefined}
            >
                <ButtonGroup value={reservation.dateOption} fullWidth options={dateOptions} onChange={handleDate} />
                <DateField
                    isOpen={isDatePickerOpen}
                    name="date"
                    value={reservation.date}
                    shouldDisableDate={(date: DateTime) => !RestaurantUtils.hasShiftForDate(date, props.restaurant)}
                    minDate={DateTime.local()}
                    onChange={handleDate}
                    onClose={() => setDatePickerOpen(false)}
                />
            </Field>
            {Boolean(reservation.date) && (
                <>
                    <Field className="people" icon={<PeopleIcon />} label={t('reservation.fields.people')}>
                        <Participants
                            setReservation={setReservation}
                            reservation={reservation}
                            restaurant={props.restaurant}
                        />
                    </Field>
                    {reservation.participants > 0 && areaOptions.length > 0 && (
                        <Field className="time" icon={<HouseWithDots />} label={t('reservation.fields.area')}>
                            <ButtonGroup
                                fullWidth
                                value={reservation.areaId}
                                options={areaOptions}
                                onChange={handleArea}
                            />
                        </Field>
                    )}
                </>
            )}

            {!props.isLoading && reservation.date && reservation.participants > 0 && areaOptions.length === 0 && (
                <Label type="text" value={t('reservation.datetime.time.noTimeSlotsAvailable')} align="center" />
            )}
            {selectedArea && (
                <Field className="hour" icon={<ClockIcon />} label={t('reservation.fields.time')}>
                    {slotOptions.length > 0 && (
                        <div className="widget-grid">
                            <ButtonGroup
                                value={reservation.time}
                                options={reservation.showAllTimes ? slotOptions : slotOptions.slice(0, defaultSlots)}
                                onChange={handleTime}
                            />
                        </div>
                    )}
                    {!reservation.showAllTimes && slotOptions.length > defaultSlots && (
                        <button
                            type="button"
                            className="load-more"
                            onClick={() => {
                                setReservation((prev) => {
                                    return {
                                        ...prev,
                                        showAllTimes: true,
                                    };
                                });
                            }}
                        >
                            <AddCircleIcon />
                            {t('reservation.datetime.time.loadMore')}
                        </button>
                    )}
                    {!props.isLoading && slotOptions.length === 0 && (
                        <Label type="text" value={t('reservation.datetime.time.noTimeSlotsAvailable')} align="center" />
                    )}
                </Field>
            )}
            <div className="grow" />
            {reservation.time !== '' && (
                <div className="buttons">
                    <Button label={t('common.buttons.next')} onClick={handleNext} />
                </div>
            )}
        </>
    );
};

const actions: IActionProps = {
    requestRestaurantAvailableTimes: restaurantActions.restaurantAvailableTimesRequested,
    saveReservationDateTime: reservationActions.saveReservationDateTime,
    getShifts: restaurantActions.shiftsRequested,
};

const mapStateToProps = (state: IApplicationStore): IStoreProps => ({
    reservation: state.reservation.datetime,
    isLoading: state.restaurant.isLoading,
    shifts: state.restaurant.availableShifts,
});

export default connect(mapStateToProps, actions)(ParticipantsDateTime);
